import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { QRCodeSVG } from 'qrcode.react';
import apiClient from '../../../../../../api/apiClient';
import { useConfirmationModalStore, usePreloaderStore } from '../../../../../../stores';
import { Endpoints, Defaults } from '../../../../../../views/democracy/constants';
import { buildJoinUrl, copyToClipboard, extractErrorMessage } from '../../../../../../views/democracy/utils';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';

const SWATCH_STYLE = { width: 44, height: 44, padding: 0, border: '1px solid var(--line-2)', borderRadius: 9, cursor: 'pointer', background: 'none' };

// Модалка редактирования команды (в настройках игры): поля, аудио, joinCode/QR.
export default function TeamEditModal({ team, isDevelop, onClose, onChanged }) {
    const { enqueueSnackbar } = useSnackbar();
    const { open: openConfirmation } = useConfirmationModalStore();
    const { show, hide } = usePreloaderStore();

    const [data, setData] = useState({
        orderId: team.orderId,
        name: team.name || '',
        color: team.color || Defaults.teamColor,
        initialBalance: team.initialBalance ?? Defaults.initialBalance,
        initialRating: team.initialRating ?? Defaults.initialRating
    });
    const [joinCode, setJoinCode] = useState(team.joinCode || '');

    const setField = (field, value) => setData(prev => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        show();
        try {
            await apiClient.put(Endpoints.teams.update(team.id), {
                orderId: parseInt(data.orderId, 10),
                name: data.name,
                color: data.color,
                initialBalance: parseInt(data.initialBalance, 10) || 0,
                initialRating: parseInt(data.initialRating, 10) || 0
            });
            enqueueSnackbar('Сохранено', { variant: 'success' });
            onChanged();
            onClose();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось сохранить команду'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const handleDelete = () => {
        openConfirmation(`Удалить команду «${team.name}»? Это действие нельзя отменить.`, async () => {
            show();
            try {
                await apiClient.delete(Endpoints.teams.delete(team.id));
                enqueueSnackbar('Команда удалена', { variant: 'success' });
                onChanged();
                onClose();
            } catch (error) {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось удалить'), { variant: 'error' });
            } finally {
                hide();
            }
        });
    };

    const handleRegenerate = async () => {
        show();
        try {
            const response = await apiClient.post(Endpoints.teams.regenerateJoinCode(team.id));
            setJoinCode(response.data.joinCode);
            enqueueSnackbar('joinCode перегенерирован', { variant: 'success' });
            onChanged();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось перегенерировать'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const handleCopy = async () => {
        const ok = await copyToClipboard(joinCode);
        enqueueSnackbar(ok ? 'joinCode скопирован' : 'Не удалось скопировать', { variant: ok ? 'success' : 'warning' });
    };

    const joinUrl = buildJoinUrl(joinCode);
    // QR зависит только от joinUrl — без useMemo он перегенерировался на каждый ввод в полях формы.
    const joinQr = useMemo(() => (joinCode ? <div className="qr"><QRCodeSVG value={joinUrl} size={150} /></div> : null), [joinCode, joinUrl]);

    const footer = (
        <>
            {isDevelop && <button type="button" className="btn btn-danger" onClick={handleDelete}>Удалить</button>}
            <button type="button" className="btn btn-primary" onClick={handleSave}>Сохранить</button>
        </>
    );

    return (
        <Modal open title="Редактирование команды" onClose={onClose} size="lg" footer={footer}>
            <div className="row-3">
                <LabeledInput label="Порядок" value={String(data.orderId)} onChange={(e) => setField('orderId', e.target.value)} />
                <LabeledInput label="Название" value={data.name} onChange={(e) => setField('name', e.target.value)} />
                <div>
                    <label className="ctl-label">Цвет</label>
                    <div className="tmg-color">
                        <input type="color" style={SWATCH_STYLE} value={data.color} onChange={(e) => setField('color', e.target.value)} aria-label="Цвет" />
                        <input className="input mono" value={data.color} onChange={(e) => setField('color', e.target.value)} />
                    </div>
                </div>
            </div>

            <div className="set-row2">
                <LabeledInput label="Стартовый баланс" value={String(data.initialBalance)} onChange={(e) => setField('initialBalance', e.target.value)} />
                <LabeledInput label="Стартовый рейтинг" value={String(data.initialRating)} onChange={(e) => setField('initialRating', e.target.value)} />
            </div>

            <div className="tmg-join">
                <div>
                    <div className="lbl">joinCode подключения игрока</div>
                    <div className="code">{joinCode || '—'}</div>
                    <div className="acts">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={handleCopy} disabled={!joinCode}>Скопировать</button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={handleRegenerate}>Перегенерировать</button>
                    </div>
                    {joinCode && <div className="url">{joinUrl}</div>}
                </div>
                {joinQr}
            </div>
        </Modal>
    );
}

TeamEditModal.propTypes = {
    team: PropTypes.object.isRequired,
    isDevelop: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onChanged: PropTypes.func.isRequired
};
